<template>
  <div id="app-root">
    <!-- Announces the page to screen readers on client-side navigation. -->
    <p class="route-announce" aria-live="assertive">{{ routeAnnounce }}</p>
    <ToastHost />
    <template v-if="isPublicRoute">
      <router-view />
    </template>
    <template v-else>
      <div class="layout">
        <!-- Mobile top bar (only < 768px): hamburger opens the sidebar drawer -->
        <header class="mobile-topbar">
          <button class="hamburger" :aria-label="'menu'" @click="mobileNavOpen = true">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
          </button>
          <span class="mt-brand">
            <svg width="22" height="22" viewBox="0 0 36 36" fill="none"><rect width="36" height="36" rx="8" fill="var(--accent)"/><path d="M10 14h16M10 18h10M10 22h13" stroke="#fff" stroke-width="2.5" stroke-linecap="round"/></svg>
            DevTask
          </span>
        </header>
        <div v-if="mobileNavOpen" class="nav-backdrop" @click="mobileNavOpen = false"></div>
        <AppSidebar :mobile-open="mobileNavOpen" @logout="handleLogout" />
        <ProjectNav v-if="showProjectNav" />
        <main class="main-content">
          <AppTopbar />
          <div class="wrap">
            <router-view />
          </div>
        </main>
      </div>
    </template>
  </div>
</template>

<script>
import { computed, ref, watch, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AppSidebar from '@/components/AppSidebar.vue'
import ProjectNav from '@/components/ProjectNav.vue'
import AppTopbar from '@/components/AppTopbar.vue'
import ToastHost from '@/components/ToastHost.vue'
import { useAuth } from '@/composables/useAuth'
import { initRealtime, teardownRealtime } from '@/composables/useRealtime'
import { useIdleTimeout, clearIdleStamp } from '@/composables/useIdleTimeout'
import api from '@/api'

export default {
  name: 'App',
  components: { AppSidebar, ProjectNav, AppTopbar, ToastHost },
  setup() {
    const route  = useRoute()
    const router = useRouter()
    const { clearSession, setUser, currentUser, state } = useAuth()

    const isPublicRoute = computed(() => route.meta.public)
    // The project rail docks beside the sidebar only while browsing tasks.
    const showProjectNav = computed(() => route.name === 'tasks')

    // Mobile nav drawer — close on any navigation.
    const mobileNavOpen = ref(false)
    watch(() => route.fullPath, () => { mobileNavOpen.value = false })

    // Mirror the document title into a live region so SR users hear the new page
    // on navigation (the router's afterEach sets document.title first).
    const routeAnnounce = ref('')
    watch(() => route.fullPath, () => {
      nextTick(() => { routeAnnounce.value = document.title })
    }, { immediate: true })

    // Connect realtime when logged in (internal users have an office_id).
    function connectRealtime() {
      const u = currentUser()
      if (u && u.office_id) initRealtime(u.office_id)
    }
    if (state.token) connectRealtime()
    watch(() => state.token, (tok) => { tok ? connectRealtime() : teardownRealtime() })

    async function handleLogout() {
      try { await api.logout() } catch {}
      teardownRealtime()
      clearIdleStamp()
      clearSession()
      router.push({ name: 'login' })
    }

    // Local logout used by the idle timer and the startup token check — no server
    // round-trip needed (the token is already gone/invalid).
    function forceLogout() {
      teardownRealtime()
      clearIdleStamp()
      clearSession()
      if (route.name !== 'login') router.push({ name: 'login' })
    }

    // Auto-logout after 30 min idle (and immediately if reopened after the window).
    useIdleTimeout(() => !!state.token, forceLogout)

    // On load, verify the saved token is still valid server-side AND refresh the
    // cached user from the authoritative record. This reverts any client-side
    // tampering of role/flags in localStorage (defence-in-depth — the API already
    // re-derives role from the token, so a forged flag never grants access).
    // A revoked/expired token logs the UI out instead of lingering until the next call.
    if (state.token) {
      api.me()
        .then((res) => { if (res && res.data) setUser(res.data) })
        .catch((e) => { if (e && e.status === 401) forceLogout() })
    }

    return { isPublicRoute, showProjectNav, handleLogout, mobileNavOpen, routeAnnounce }
  },
}
</script>

<style scoped>
/* Visually hidden, but present for assistive tech (route announcer). */
.route-announce {
  position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px;
  overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; border: 0;
}
</style>
